import { App } from './app/App.js'

const TEMPLATE_HTML = `
<div class="p3d-viewholder">
    <canvas class="p3d-webgl" layoutsubtree>
        <div class="p3d-screen-content"></div>
    </canvas>
</div>
`

const TEMPLATE_IMAGE = `
<div class="p3d-viewholder">
    <canvas class="p3d-webgl"></canvas>
</div>
`

const DEFAULT_SCREEN_HTML = `
<div class="status-bar"><span>9:41</span><span>5G</span></div>
<header class="hero">
    <h1>Hello</h1>
    <p class="subtitle">from three-html-render</p>
</header>
<div class="section-indicator">Section 0</div>
<button class="cta" type="button">Tap me</button>
`

const STYLE_ID = 'phone-in-threed-styles'
const CSS = `
.p3d-root { position: relative; width: 100%; height: 100%; }
.p3d-viewholder { position: absolute; inset: 0; outline: none; }
.p3d-webgl { width: 100%; height: 100%; display: block; }
.p3d-controls {
    position: fixed; top: 12px; right: 12px; z-index: 10000;
    padding: 12px 16px; background: rgba(17,24,39,0.75);
    border-radius: 10px; color: #f3f4f6;
    font: 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    backdrop-filter: blur(6px); min-width: 220px;
}
.p3d-controls label { display: block; margin-bottom: 10px; }
.p3d-controls input[type="range"] { width: 100%; }
.p3d-export-btn, .p3d-upload-btn {
    position: fixed; bottom: 16px; z-index: 10000;
    padding: 10px 18px; color: #fff;
    border: none; border-radius: 8px; cursor: pointer;
    font: 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-shadow: 0 4px 12px rgba(0,0,0,0.25);
}
.p3d-export-btn { right: 16px; background: #2563eb; }
.p3d-export-btn:hover { background: #1d4ed8; }
.p3d-upload-btn { right: 140px; background: #059669; }
.p3d-upload-btn:hover { background: #047857; }
`

function injectStyles() {
    if (document.getElementById(STYLE_ID)) return
    const style = document.createElement('style')
    style.id = STYLE_ID
    style.textContent = CSS
    document.head.appendChild(style)
}

const CONTROLS = [
    { key: 'ambient',     label: 'Ambient',     min: 0, max: 3, step: 0.01, value: 0.4 },
    { key: 'directional', label: 'Directional', min: 0, max: 5, step: 0.01, value: 1.54 },
    { key: 'env',         label: 'Environment', min: 0, max: 3, step: 0.01, value: 1.6 },
    { key: 'exposure',    label: 'Exposure',    min: 0, max: 3, step: 0.01, value: 0.81 },
    { key: 'contrast',    label: 'Contrast',    min: 0.5, max: 2.5, step: 0.01, value: 1.22 },
    { key: 'saturation',  label: 'Saturation',  min: 0, max: 3, step: 0.01, value: 1.12 },
    { key: 'shadows',     label: 'Shadows',     type: 'checkbox', value: true },
]

function buildControls(phone) {
    const panel = document.createElement('div')
    panel.className = 'p3d-controls'

    for (const c of CONTROLS) {
        const row = document.createElement('label')
        if (c.type === 'checkbox') {
            const cb = document.createElement('input')
            cb.type = 'checkbox'
            cb.checked = !!c.value
            cb.addEventListener('change', () => apply(c.key, cb.checked))
            row.appendChild(cb)
            row.appendChild(document.createTextNode(' ' + c.label))
        } else {
            const title = document.createElement('span')
            const valEl = document.createElement('span')
            valEl.textContent = c.value.toFixed(2)
            title.textContent = c.label + ': '
            title.appendChild(valEl)
            const input = document.createElement('input')
            input.type = 'range'
            input.min = c.min; input.max = c.max; input.step = c.step; input.value = c.value
            input.addEventListener('input', () => {
                const v = parseFloat(input.value)
                valEl.textContent = v.toFixed(2)
                apply(c.key, v)
            })
            row.appendChild(title)
            row.appendChild(input)
        }
        panel.appendChild(row)
    }
    document.body.appendChild(panel)

    function apply(key, v) {
        switch (key) {
            case 'ambient':     if (phone.lights?.ambient) phone.lights.ambient.intensity = v; break
            case 'directional': if (phone.lights?.key) phone.lights.key.intensity = v; break
            case 'env':         if (phone.scene) phone.scene.environmentIntensity = v; break
            case 'exposure':    if (phone.renderer) phone.renderer.toneMappingExposure = v; break
            case 'contrast':    phone.app?.htmlPlane?.setContrast(v); break
            case 'saturation':  phone.app?.htmlPlane?.setSaturation(v); break
            case 'shadows':     phone.setShadowsEnabled(v); break
        }
    }
    return panel
}

export class PhoneInThreed {
    constructor(opts = {}) {
        const options = typeof opts === 'string' ? { target: opts } : opts
        const {
            target = null,
            assetsBase = '',
            content = DEFAULT_SCREEN_HTML,
            enableStats = false,
            enableShadows = true,
            imageTexture = null,
            maskTexture = null,
            orbitControls = false,
            autoMove = true,
            exportButton = false,
            uploadButton = false,
        } = options
        this._enableShadows = enableShadows
        this._imageTexture = imageTexture
        this._maskTexture = maskTexture
        this._orbitControls = orbitControls
        this._autoMove = autoMove
        this._exportButton = exportButton
        this._uploadButton = uploadButton

        injectStyles()

        this.el = document.createElement('div')
        this.el.className = 'p3d-root'
        this.el.innerHTML = imageTexture ? TEMPLATE_IMAGE : TEMPLATE_HTML

        if (!imageTexture) {
            const screenContent = this.el.querySelector('.p3d-screen-content')
            if (typeof content === 'string') {
                screenContent.innerHTML = content
            } else if (content instanceof Node) {
                screenContent.appendChild(content)
            }
        }

        if (target) {
            const host = typeof target === 'string' ? document.querySelector(target) : target
            host?.appendChild(this.el)
        }

        // Defer start so the caller can attach .el (the polyfill reads layout).
        queueMicrotask(() => this._start({
            assetsBase,
            enableStats,
            enableShadows: this._enableShadows,
            imageTexture: this._imageTexture,
            maskTexture: this._maskTexture,
            orbitControls: this._orbitControls,
            autoMove: this._autoMove,
            exportButton: this._exportButton,
            uploadButton: this._uploadButton,
        }))
    }

    async _start({ assetsBase, enableStats, enableShadows, imageTexture, maskTexture, orbitControls, autoMove, exportButton, uploadButton }) {
        const canvas = this.el.querySelector('canvas.p3d-webgl')
        const container = this.el.querySelector('.p3d-viewholder')

        this.htmlScreen = null
        if (!imageTexture) {
            const { HtmlScreen } = await import('./app/HtmlScreen.js')
            const screenContent = this.el.querySelector('.p3d-screen-content')
            this.htmlScreen = new HtmlScreen(canvas, { content: screenContent })
        }
        this.app = new App({
            canvas,
            container,
            assetsBase,
            htmlScreen: this.htmlScreen,
            enableStats,
            enableShadows,
            imageTexture,
            maskTexture,
            orbitControls,
            autoMove,
        })

        if (enableStats) this._controlsEl = buildControls(this)
        if (exportButton) this._exportBtnEl = this._buildExportButton()
        if (uploadButton) this._uploadBtnEl = this._buildUploadButton()
    }

    _buildExportButton() {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'p3d-export-btn'
        btn.textContent = 'Export PNG'
        btn.addEventListener('click', () => this.exportImage())
        document.body.appendChild(btn)
        return btn
    }

    _buildUploadButton() {
        const btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'p3d-upload-btn'
        btn.textContent = 'Upload image'

        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.style.display = 'none'
        input.addEventListener('change', () => {
            const file = input.files?.[0]
            if (file) this.setImageTexture(file)
            input.value = ''
        })

        btn.addEventListener('click', () => input.click())
        document.body.appendChild(btn)
        document.body.appendChild(input)
        return btn
    }

    setImageTexture(source) {
        this.app?.setImageTexture(source)
    }

    setMaskTexture(source) {
        this.app?.setMaskTexture(source)
    }

    exportImage(filename = 'phone.png') {
        return this.app?.exportImage(filename)
    }

    setShadowsEnabled(v) {
        this.app?.setShadowsEnabled(v)
    }

    get scene() { return this.app?.scene }
    get camera() { return this.app?.camera }
    get renderer() { return this.app?.renderer }
    get lights() { return this.app?.lights }
    get phone() { return this.app?.phone }

    rotate() { this.app?.phone?.rotate() }
    setSection(n) {
        this.app?.onNewSection(n)
        this.htmlScreen?.setSection(n)
    }
}
